import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import addAtomsToInventory from '@/shared/api/inventory/addAtomsToInventory';
import updateInventoryById from '@/shared/api/inventory/updateInventoryById';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  inventoryId: string;
  isFormValid: boolean;
  inventoryDataToSave: {
    [key: string]: unknown;
    atomic_metadata: Record<string, unknown>;
  } | null;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
  setRefreshTableIndex: Dispatch<SetStateAction<number>>;
};

const useUpdateInventory = ({
  inventoryId,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
  setRefreshTableIndex,
}: Args) => {
  const { t } = useTranslation(['inventory', 'common']);
  const redirectLogin = useRedirectLogin();

  const onInventoryUpdate = async (details: Record<string, unknown>) => {
    onUpdateProgressDialog({
      title: t('inventory:updatingInventory'),
      open: true,
      progress: 0,
    });

    const response = await updateInventoryById({
      inventoryId,
      body: details,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('inventory:saveChangesEditSuccess'),
      });
    } else {
      onUpdateProgressDialog({ open: false });
    }
  };

  const onAddItemsToInventory = async (details: Record<string, unknown>) => {
    const INVENTORY_ITEMS_LIMIT = 1000;
    const qty = details.qty as number;

    const calculateQty = (qty: number, limit: number) => {
      if (qty === limit) return qty;
      if (qty > limit) return qty - limit;
      return qty;
    };

    const addInventoryItemsResponse = await addAtomsToInventory({
      inventoryId,
      body: details,
      qty: calculateQty(qty, INVENTORY_ITEMS_LIMIT),
      onUnauthorized: redirectLogin,
      onProgress: (progress) => onUpdateProgressDialog({ progress }),
    });

    onUpdateProgressDialog({ open: false });

    if (addInventoryItemsResponse.every((response) => response.success)) {
      onUpdateNotificationBar({
        open: true,
        message: t('inventory:saveChangesEditSuccess'),
      });
      setRefreshTableIndex((prev) => prev + 1);
    }
  };

  // useEffect(() => {
  //   if (isFormValid && inventoryDataToSave) {
  //     const createNewInventory = async () => {
  //       onUpdateProgressDialog({
  //         title: t('inventory:updatingInventory'),
  //         open: true,
  //         progress: 0,
  //       });

  //       const INVENTORY_ITEMS_LIMIT = 1000;

  //       const qty = inventoryDataToSave.qty as number;

  //       // do not send `qty` & `atomic_metadata` in the update request.
  //       const body = {
  //         ...inventoryDataToSave,
  //         qty: undefined,
  //         atomic_metadata: undefined,
  //       };

  //       const response = await updateInventoryById({
  //         inventoryId,
  //         body,
  //         onUnauthorized: redirectLogin,
  //       });

  //       if (response.success) {
  //         if (!('qty' in inventoryDataToSave) || qty === 0) {
  //           onUpdateProgressDialog({ open: false });
  //           onUpdateNotificationBar({
  //             open: true,
  //             message: t('inventory:saveChangesEditSuccess'),
  //           });
  //           return;
  //         }

  //         const calculateQty = (qty: number, limit: number) => {
  //           if (qty === limit) return qty;
  //           if (qty > limit) return qty - limit;
  //           return qty;
  //         };

  //         const addInventoryItemsResponse = await addAtomsToInventory({
  //           inventoryId: (response.data as InventoryGetModel).inventory_id,
  //           body: inventoryDataToSave.atomic_metadata,
  //           qty: calculateQty(qty, INVENTORY_ITEMS_LIMIT),
  //           onUnauthorized: redirectLogin,
  //           onProgress: (progress) => onUpdateProgressDialog({ progress }),
  //         });

  //         if (addInventoryItemsResponse.every((response) => response.success)) {
  //           onUpdateProgressDialog({ open: false });
  //           onUpdateNotificationBar({
  //             open: true,
  //             message: t('inventory:saveChangesEditSuccess'),
  //           });
  //           setShouldUpdateTable(true);
  //         } else {
  //           onUpdateProgressDialog({ open: false });
  //         }
  //       } else {
  //         onUpdateProgressDialog({ open: false });
  //       }
  //     };

  //     void createNewInventory();
  //   }
  // }, [
  //   inventoryId,
  //   isFormValid,
  //   inventoryDataToSave,
  //   onUpdateNotificationBar,
  //   onUpdateProgressDialog,
  //   redirectLogin,
  //   setShouldUpdateTable,
  //   t,
  // ]);

  return {
    onInventoryUpdate,
    onAddItemsToInventory,
  };
};

export default useUpdateInventory;
